import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import { usePageContent } from "@/hooks/usePageContent";

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

const strategies = [
  {
    icon: "ri-building-2-line",
    title: "Joint Development (JDA)",
    badge: "Best for High Value Land",
    desc: "Partner with builders for apartments, villas, or commercial projects. You contribute the land — the developer funds construction. You receive a pre-agreed share of the built-up area or cash equivalent, without investing a rupee.",
    ideal: "Plots 1,000 sq.m+ in urban or semi-urban areas",
    roi: "35–45% share of developed area",
    timeline: "12–24 months",
    image: "https://readdy.ai/api/search-image?query=Luxury%20modern%20residential%20apartment%20complex%20construction%20site%20at%20golden%20hour%2C%20cranes%20and%20scaffolding%2C%20dramatic%20dark%20sky%2C%20warm%20amber%20building%20lights%2C%20cinematic%20real%20estate%20photography%2C%20deep%20teal%20and%20gold%20color%20grading%2C%20India%20urban%20development%2C%20highly%20detailed%20architecture&width=800&height=500&seq=strat-jda-01&orientation=landscape",
  },
  {
    icon: "ri-time-line",
    title: "Long-term Lease",
    badge: "Best for Recurring Income",
    desc: "Your land becomes a long-term income generator. Warehouse operators, logistics firms, dark store chains, EV charging hubs, and data centers pay stable rent for 9+ years. No construction needed from your side — they build, you earn.",
    ideal: "Highway plots, industrial zones, peri-urban land 2,000 sq.ft+",
    roi: "6–10% p.a. rental yield",
    timeline: "3–6 months to income",
    image: "https://readdy.ai/api/search-image?query=Large%20modern%20logistics%20warehouse%20complex%20at%20dusk%2C%20dramatic%20dark%20sky%2C%20illuminated%20loading%20bays%2C%20trucks%20in%20foreground%2C%20wide%20angle%20aerial%20perspective%2C%20cinematic%20industrial%20photography%2C%20teal%20and%20amber%20color%20palette%2C%20India%20logistics%20park%2C%20highly%20detailed&width=800&height=500&seq=strat-lease-02&orientation=landscape",
  },
  {
    icon: "ri-store-line",
    title: "Retail & Franchise",
    badge: "Best for Roadside Plots",
    desc: "High-footfall roadside plots are goldmines. Our Brand Match Algorithm connects you directly with QSR chains, fuel companies, supermarkets, and drive-thru operators actively seeking locations. No brokerage, no cold calls — direct brand placement.",
    ideal: "NH/SH highway frontage, high-visibility corner plots",
    roi: "15–25% property value increase",
    timeline: "60–90 days to lease signing",
    image: "https://readdy.ai/api/search-image?query=Modern%20QSR%20drive-thru%20restaurant%20and%20fuel%20station%20at%20night%2C%20glowing%20brand%20signs%2C%20busy%20highway%20intersection%2C%20dramatic%20dark%20moody%20atmosphere%2C%20neon%20light%20reflections%2C%20cinematic%20commercial%20photography%2C%20deep%20teal%20and%20gold%20accent%20lighting%2C%20India%20roadside%20development&width=800&height=500&seq=strat-retail-03&orientation=landscape",
  },
  {
    icon: "ri-leaf-line",
    title: "Land-as-Equity",
    badge: "Best for Scenic / Remote",
    desc: "Convert remote or scenic land into equity in a revenue-generating business — eco-resorts, glamping parks, agritourism ventures, solar farms, or organic farming projects. You retain ownership and earn a share of business profits.",
    ideal: "Remote land, scenic properties, large agricultural plots 5 acres+",
    roi: "25-year solar income or profit share",
    timeline: "6–18 months to launch",
    image: "https://readdy.ai/api/search-image?query=Luxury%20glamping%20eco%20resort%20with%20transparent%20dome%20tents%20at%20twilight%2C%20forest%20surroundings%2C%20warm%20glowing%20interiors%2C%20dramatic%20dark%20moody%20sky%2C%20stars%20visible%2C%20cinematic%20travel%20photography%2C%20deep%20teal%20and%20amber%20color%20palette%2C%20India%20nature%20retreat%2C%20highly%20detailed&width=800&height=500&seq=strat-eco-04&orientation=landscape",
  },
];

const comparison = [
  { feature: "Ownership Retained", jda: "Yes", lease: "Yes", retail: "Yes", equity: "Yes" },
  { feature: "Upfront Investment", jda: "Zero", lease: "Zero", retail: "Zero", equity: "Zero" },
  { feature: "Income Type", jda: "Lump + Recurring", lease: "Monthly Rent", retail: "Monthly Rent", equity: "Profit Share" },
  { feature: "Typical Timeline", jda: "12–24 months", lease: "3–6 months", retail: "2–4 months", equity: "6–18 months" },
  { feature: "Best Plot Size", jda: "1,000 sq.m+", lease: "2,000 sq.ft+", retail: "3,000 sq.ft+", equity: "5 acres+" },
  { feature: "Risk Level", jda: "Medium", lease: "Low", retail: "Low", equity: "Medium" },
];

export default function StrategiesPage() {
  const { getContent } = usePageContent('strategies');
  const heroBadge = stripHtml(getContent('strategies-hero-badge')) || 'Strategies';
  const heroSub = stripHtml(getContent('strategies-hero-sub')) || 'Every idle plot has a perfect strategy. AI picks the right one for you.';
  const comparisonHeading = stripHtml(getContent('strategies-comparison-heading')) || 'Strategy at a Glance';
  const ctaHeading = stripHtml(getContent('strategies-cta-heading')) || 'Not Sure Which Strategy Fits?';
  const ctaSub = stripHtml(getContent('strategies-cta-sub')) || 'Our AI Feasibility Report analyzes your plot and recommends the top 3 monetization paths — ranked by ROI.';

  return (
    <PageLayout>
      <PageHero
        badge={heroBadge}
        title="4 Powerful"
        highlight="Ways to Monetize"
        subtitle={heroSub}
        image="https://readdy.ai/api/search-image?query=Aerial%20collage%20of%20diverse%20real%20estate%20development%20projects%20in%20India%20including%20apartments%20warehouse%20and%20retail%2C%20dramatic%20golden%20hour%20lighting%2C%20dark%20moody%20cinematic%20atmosphere%2C%20deep%20teal%20sky%2C%20highly%20detailed%20aerial%20photography%2C%20warm%20amber%20accent%20lights&width=1800&height=800&seq=strat-hero-01&orientation=landscape"
      />

      {/* Strategies */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="space-y-16">
            {strategies.map((s, i) => (
              <div
                key={s.title}
                className={`grid md:grid-cols-2 gap-10 items-center ${
                  i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <span
                    className={`inline-block text-xs font-medium px-3 py-1 rounded-full mb-4 bg-primary-500/10 text-primary-700`}
                  >
                    {s.badge}
                  </span>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30">
                      <i className={`${s.icon} text-2xl text-primary-500`} />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950">
                      {s.title}
                    </h2>
                  </div>
                  <p className="text-foreground-600 leading-relaxed mb-6">{s.desc}</p>
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="p-3 rounded-xl bg-background-200/50 border border-background-300/30">
                      <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">
                        ROI
                      </div>
                      <div className="text-sm text-foreground-950 font-medium mt-1">{s.roi}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-background-200/50 border border-background-300/30">
                      <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">
                        Timeline
                      </div>
                      <div className="text-sm text-foreground-950 font-medium mt-1">{s.timeline}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-background-200/50 border border-background-300/30">
                      <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">
                        Ideal For
                      </div>
                      <div className="text-sm text-foreground-950 font-medium mt-1">{s.ideal}</div>
                    </div>
                  </div>
                  <Link
                    to="/feasibility-report"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Explore This Strategy
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Comparison
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              {comparisonHeading}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-primary-900/30">
                  <th className="text-left py-4 px-4 text-foreground-500 font-medium">Feature</th>
                  <th className="text-center py-4 px-4 text-foreground-500 font-medium">JDA</th>
                  <th className="text-center py-4 px-4 text-foreground-500 font-medium">Lease</th>
                  <th className="text-center py-4 px-4 text-foreground-500 font-medium">Retail</th>
                  <th className="text-center py-4 px-4 text-foreground-500 font-medium">Equity</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, i) => (
                  <tr
                    key={row.feature}
                    className={`border-b border-primary-900/10 ${i % 2 === 0 ? "bg-background-200/20" : ""}`}
                  >
                    <td className="py-4 px-4 text-foreground-950 font-medium">{row.feature}</td>
                    <td className="py-4 px-4 text-center text-foreground-600">{row.jda}</td>
                    <td className="py-4 px-4 text-center text-foreground-600">{row.lease}</td>
                    <td className="py-4 px-4 text-center text-foreground-600">{row.retail}</td>
                    <td className="py-4 px-4 text-center text-foreground-600">{row.equity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            {ctaHeading}
          </h2>
          <p className="text-foreground-600 mb-8">
            {ctaSub}
          </p>
          <Link
            to="/feasibility-report"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Get My Feasible AI Report
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}